import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CaretLeft, Sun, Moon, List } from '@phosphor-icons/react';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Shared SAP-style shell for public marketing pages (Use Cases, Partners, Resources).
 * Provides the BIL brand block, theme toggle, back link, and hero container.
 */
export default function PublicPageShell({
  eyebrow,
  title,
  subtitle,
  children,
  testId,
  ctaPrimary, // { label, to } optional
  ctaSecondary, // { label, to } optional
}) {
  const { theme, toggleTheme } = useTheme();
  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid={testId}>
      {/* Shell bar */}
      <header className="w-full bg-[#f5f6fa] dark:bg-muted/30 border-b border-border">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 py-3">
          <Link to="/login" className="flex items-center gap-3" data-testid="brand-block">
            <div
              className="h-14 w-16 flex items-center justify-center text-white font-heading font-bold text-xl tracking-tight"
              style={{ background: '#002677' }}
            >
              BIL
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/login"
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors hidden sm:flex items-center gap-1"
              data-testid="back-to-home"
            >
              <CaretLeft size={14} weight="bold" /> Home
            </Link>
            <button
              onClick={toggleTheme}
              className="p-2 rounded-sm hover:bg-accent/30 transition-colors"
              aria-label="Toggle theme"
            >
              {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
            </button>
            <Link
              to="/login"
              className="p-2 rounded-sm hover:bg-accent/30 transition-colors"
              aria-label="Menu"
            >
              <List size={22} />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section
        className="py-16 lg:py-24 border-b border-border"
        style={{
          background: 'linear-gradient(135deg, #eef2ff 0%, #e6efff 40%, #dae8fb 100%)',
        }}
      >
        <div className="max-w-[1200px] mx-auto px-6 text-center">
          {eyebrow && (
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#002677] mb-3">
              {eyebrow}
            </p>
          )}
          <h1 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#0a0a0a] dark:text-foreground max-w-4xl mx-auto">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              {subtitle}
            </p>
          )}
          {(ctaPrimary || ctaSecondary) && (
            <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
              {ctaPrimary && (
                <Link
                  to={ctaPrimary.to}
                  className="h-12 px-7 rounded-sm inline-flex items-center gap-2 text-base font-medium text-white bg-[#002677] hover:bg-[#002677]/90 transition-colors"
                  data-testid="public-cta-primary"
                >
                  {ctaPrimary.label} <ArrowRight size={16} weight="bold" />
                </Link>
              )}
              {ctaSecondary && (
                <Link
                  to={ctaSecondary.to}
                  className="h-12 px-7 rounded-sm inline-flex items-center text-base font-medium border-2 border-foreground/80 hover:bg-foreground hover:text-background transition-colors"
                  data-testid="public-cta-secondary"
                >
                  {ctaSecondary.label}
                </Link>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Body */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer CTA */}
      <section className="py-12 bg-[#002677] text-white">
        <div className="max-w-[1200px] mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
          <p className="text-white/80">Ready to see BIL running your business?</p>
          <div className="flex gap-3">
            <Link to="/pricing" className="underline hover:no-underline font-medium">See pricing</Link>
            <span className="text-white/40">·</span>
            <Link to="/register" className="underline hover:no-underline font-medium">Start free</Link>
            <span className="text-white/40">·</span>
            <Link to="/support" className="underline hover:no-underline font-medium">Support</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
